import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { url } from '../../../../../env';
import {
    LEGAL_SCHEMA, LEGAL_CATEGORY_TO_TYPE, getLegalFields,
    categoryForType, deserializeFieldValue, serializeFieldValue,
} from '../shared/legalEditorSchema';
import StructuredLegalEditor from '../shared/StructuredLegalEditor';
import {
    AppTextField, AppNumberField, AppButton, AppIconButton,
} from '../../../../../Components/FormKit';

// ─── Design tokens ────────────────────────────────────────────────────────────
const TEAL = '#00897B';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const dotGet = (obj, path) =>
    path.split('.').reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);

// ─── Editable fields per category ────────────────────────────────────────────
const EDOS_CUENTA_SECTIONS = [
    { section: 'Estado de Cuenta', fields: [
        { path: 'periodo',              label: 'Periodo',                type: 'text'   },
        { path: 'saldo_inicial',        label: 'Saldo Inicial',          type: 'number', currency: true },
        { path: 'total_abonos',         label: 'Total Abonos',           type: 'number', currency: true },
        { path: 'total_cargos',         label: 'Total Cargos',           type: 'number', currency: true },
        { path: 'saldo_final',          label: 'Saldo Final',            type: 'number', currency: true },
        { path: 'saldo_promedio',       label: 'Saldo Promedio',         type: 'number', currency: true },
    ]},
];

const EDIT_SECTIONS_BY_CATEGORY = {
    'ESTADOS FINANCIEROS': [
        { section: 'Identificación', fields: [
            { path: 'empresa', label: 'Empresa', type: 'text' },
            { path: 'periodo', label: 'Periodo', type: 'text' },
        ]},
        { section: 'Balance General', fields: [
            { path: 'balance_general.total_activo',               label: 'Activo Total',                 type: 'number', currency: true },
            { path: 'balance_general.total_pasivo',               label: 'Pasivo Total',                 type: 'number', currency: true },
            { path: 'balance_general.capital_contable_reportado', label: 'Capital Contable (reportado)', type: 'number', currency: true },
            { path: 'balance_general.activo_circulante',          label: 'Activo Circulante',            type: 'number', currency: true },
            { path: 'balance_general.pasivo_circulante',          label: 'Pasivo Circulante',            type: 'number', currency: true },
        ]},
        { section: 'Estado de Resultados', fields: [
            { path: 'estado_resultados.ventas_totales',                  label: 'Ventas Totales',           type: 'number', currency: true },
            { path: 'estado_resultados.utilidad_neta',                   label: 'Utilidad Neta',            type: 'number', currency: true },
            { path: 'estado_resultados.utilidad_operacion',              label: 'Utilidad de Operación',    type: 'number', currency: true },
            { path: 'estado_resultados.utilidad_antes_impuestos',        label: 'Utilidad Antes Impuestos', type: 'number', currency: true },
            { path: 'estado_resultados.depreciacion',                    label: 'Depreciación',             type: 'number', currency: true },
            { path: 'estado_resultados.amortizacion',                    label: 'Amortización',             type: 'number', currency: true },
            { path: 'estado_resultados.depreciacion_amortizacion_total', label: 'Dep. + Amort. Total',      type: 'number', currency: true },
            { path: 'estado_resultados.gastos_financieros',              label: 'Gastos Financieros',       type: 'number', currency: true },
        ]},
    ],
    'EDOS CUENTA':     EDOS_CUENTA_SECTIONS,
    'EDO CUENTA AVAL': EDOS_CUENTA_SECTIONS,
    // Legal categories (ACTA CONSTI, PODERES, REGISTRO PUBLICO) now use LEGAL_SCHEMA from shared module
};

// ─── Edit form component (defined outside to avoid remount) ──────────────────
const DiscrepanciaEditForm = ({
    sections, editValues, activeOverridePaths, missingRequiredPaths = [], saveReason, saving,
    onFieldChange, onClearPath, onSaveReasonChange, onSave, onCancel, aiValues = {},
}) => {
    const [confirmRevert, setConfirmRevert] = useState(null); // { path, label, aiValue }
    return (
        <div style={{ position: 'relative' }}>
            {sections.map(({ section, fields }) => (
                <div key={section} style={{ marginBottom: '1rem' }}>
                    <div style={{
                        fontSize: '0.68rem', fontWeight: '700', color: '#64748b',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        paddingBottom: '0.3rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.6rem',
                    }}>
                        {section}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.55rem 0.75rem' }}>
                    {fields.map(({ path, label, type, currency: isCurrency }) => {
                        const isConfirmed = activeOverridePaths.includes(path);
                        const isMissing   = !isConfirmed && missingRequiredPaths.includes(path);
                        const customVars = isConfirmed
                            ? { '--fk-border': TEAL, '--fk-bg': `${TEAL}08` }
                            : isMissing
                                ? { '--fk-border': '#fca5a5', '--fk-bg': '#fff5f5' }
                                : undefined;
                        const trailing = (
                            <>
                                {isMissing && (
                                    <span style={{
                                        fontSize: '0.65rem', backgroundColor: '#fee2e2', color: '#dc2626',
                                        padding: '0.1rem 0.4rem', borderRadius: '9999px', fontWeight: '600',
                                    }}>Requerido</span>
                                )}
                                {isConfirmed && (
                                    <span style={{
                                        fontSize: '0.65rem', backgroundColor: `${TEAL}18`, color: TEAL,
                                        padding: '0.1rem 0.4rem', borderRadius: '9999px', fontWeight: '600',
                                    }}>✓ Confirmado</span>
                                )}
                                {isConfirmed && (
                                    <AppIconButton
                                        icon="pi pi-replay"
                                        onClick={() => setConfirmRevert({ path, label, aiValue: aiValues[path] })}
                                        title="Revertir a valor IA"
                                        size="0.75rem"
                                    />
                                )}
                            </>
                        );
                        if (type === 'number') {
                            return (
                                <div key={path} style={customVars}>
                                    <AppNumberField
                                        label={label}
                                        value={editValues[path] != null ? Number(editValues[path]) : null}
                                        onValueChange={e => onFieldChange(path, e.value)}
                                        placeholder={isMissing ? '← Requerido' : ''}
                                        invalid={isMissing}
                                        trailing={trailing}
                                        mode={isCurrency ? 'currency' : undefined}
                                        currency={isCurrency ? 'MXN' : undefined}
                                        currencyDisplay={isCurrency ? 'narrowSymbol' : undefined}
                                        useGrouping={isCurrency}
                                        minFractionDigits={isCurrency ? 2 : undefined}
                                    />
                                </div>
                            );
                        }
                        return (
                            <div key={path} style={customVars}>
                                <AppTextField
                                    label={label}
                                    value={editValues[path] ?? ''}
                                    onChange={e => onFieldChange(path, e.target.value)}
                                    placeholder={isMissing ? '← Requerido' : ''}
                                    invalid={isMissing}
                                    trailing={trailing}
                                />
                            </div>
                        );
                    })}
                    </div>
                </div>
            ))}

            <div style={{ marginTop: '0.5rem' }}>
                <AppTextField
                    label="Motivo de corrección"
                    value={saveReason}
                    onChange={e => onSaveReasonChange(e.target.value)}
                    placeholder="Ej: Dato incorrecto en OCR, valor confirmado con cliente…"
                />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                <AppButton label="Cancelar" variant="secondary" size="sm" onClick={onCancel} disabled={saving} />
                <AppButton label="Guardar correcciones" variant="primary" size="sm" onClick={onSave} loading={saving} />
            </div>

            {/* Overlay de confirmación de reversión */}
            <AnimatePresence>
            {confirmRevert && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{
                        position: 'absolute', inset: 0,
                        backgroundColor: 'rgba(255,255,255,0.88)',
                        backdropFilter: 'blur(3px)',
                        borderRadius: '0.75rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 20,
                    }}
                >
                    <motion.div
                        initial={{ scale: 0.92, y: 6 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.92, y: 6 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        style={{
                            backgroundColor: '#fff', border: '1px solid #e2e8f0',
                            borderRadius: '0.625rem', padding: '1.25rem 1.5rem',
                            maxWidth: '280px', textAlign: 'center',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        }}
                    >
                        <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.35rem' }}>
                            ¿Revertir campo?
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.6rem' }}>
                            <strong>{confirmRevert.label}</strong> regresará al valor de la IA:
                        </div>
                        <div style={{
                            backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem', padding: '0.45rem 0.75rem',
                            marginBottom: '1rem', fontSize: '0.875rem',
                            fontWeight: '600', color: '#0f172a',
                        }}>
                            {confirmRevert.aiValue !== null && confirmRevert.aiValue !== undefined
                                ? String(confirmRevert.aiValue)
                                : <span style={{ color: '#94a3b8', fontWeight: '400', fontStyle: 'italic' }}>Sin valor (vacío)</span>
                            }
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <AppButton label="Cancelar" variant="secondary" size="sm" onClick={() => setConfirmRevert(null)} />
                            <AppButton label="Revertir" variant="primary" size="sm"
                                       onClick={() => { onClearPath(confirmRevert.path); setConfirmRevert(null); }} />
                        </div>
                    </motion.div>
                </motion.div>
            )}
            </AnimatePresence>
        </div>
    );
};

// ─── Main modal ───────────────────────────────────────────────────────────────
const DiscrepanciaModal = ({ isOpen, onClose, item, persona, onSaved }) => {
    const [overrideAudit, setOverrideAudit]         = useState(null);
    const [editMode, setEditMode]                   = useState(false);
    const [editValues, setEditValues]               = useState({});
    const [activeOverridePaths, setActiveOverridePaths] = useState([]);
    const [clearPathsQueue, setClearPathsQueue]     = useState([]);
    const [saveReason, setSaveReason]               = useState('');
    const [saving, setSaving]                       = useState(false);
    const [loadingOverrides, setLoadingOverrides]   = useState(false);

    const extractedJson = item?.original_data?.ai_analysis?.extracted_json
        || item?.original_data?.ai_analysis?.extracted || {};

    // Determine if this is a legal document (uses shared schema) or non-legal (uses local sections)
    const itemCategory = item?.category?.toUpperCase();
    const legalType = LEGAL_CATEGORY_TO_TYPE[itemCategory] || null;
    const legalSchema = legalType ? LEGAL_SCHEMA[legalType] : null;
    const isLegalItem = !!legalSchema;

    const editSections = isLegalItem ? null : (EDIT_SECTIONS_BY_CATEGORY[itemCategory] || null);
    const hasEditor = isLegalItem || !!editSections;

    // Flat map { [dotPath]: aiValue } for the revert confirmation (non-legal only)
    const aiValues = React.useMemo(() => {
        if (!editSections) return {};
        const flat = {};
        editSections.forEach(({ fields }) => {
            fields.forEach(({ path }) => { flat[path] = dotGet(extractedJson, path); });
        });
        return flat;
    }, [editSections, extractedJson]);

    // Load overrides whenever modal opens
    useEffect(() => {
        if (!isOpen || !item?.analysisItemId) {
            setOverrideAudit(null);
            setEditMode(false);
            setEditValues({});
            setActiveOverridePaths([]);
            setClearPathsQueue([]);
            setSaveReason('');
            setLoadingOverrides(false);
            return;
        }
        setLoadingOverrides(true);
        const token = localStorage.getItem('token');
        axios.get(`${url}/document-analysis/items/${item.analysisItemId}/operational-overrides`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(res => {
            setOverrideAudit(res.data.audit_summary || null);

            const overrides = res.data.overrides || [];
            const confirmedPaths = overrides.map(o => o.field_path);
            setActiveOverridePaths(confirmedPaths);

            // Init edit values: AI extracted + confirmed overrides on top
            if (isLegalItem) {
                const fields = getLegalFields(legalType);
                const values = {};
                fields.forEach((field) => {
                    const aiVal = extractedJson[field.path];
                    if (aiVal !== null && aiVal !== undefined) {
                        values[field.path] = deserializeFieldValue(field, aiVal);
                    }
                });
                overrides.forEach(o => {
                    const field = fields.find(f => f.path === o.field_path);
                    values[o.field_path] = field
                        ? deserializeFieldValue(field, o.confirmed_value)
                        : o.confirmed_value;
                });
                setEditValues(values);
            } else if (editSections) {
                const values = {};
                editSections.forEach(({ fields }) => {
                    fields.forEach(({ path }) => {
                        const aiVal = dotGet(extractedJson, path);
                        if (aiVal !== null && aiVal !== undefined) values[path] = aiVal;
                    });
                });
                overrides.forEach(o => { values[o.field_path] = o.confirmed_value; });
                setEditValues(values);
            }
        }).catch(() => {
            setOverrideAudit(null);
        }).finally(() => {
            setLoadingOverrides(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, item?.analysisItemId]);

    const handleFieldChange = useCallback((path, value) => {
        setEditValues(v => ({ ...v, [path]: value }));
    }, []);

    const handleClearPath = useCallback((path) => {
        setClearPathsQueue(q => q.includes(path) ? q : [...q, path]);
        setActiveOverridePaths(p => p.filter(x => x !== path));
        setEditValues(v => {
            const next = { ...v };
            const aiVal = dotGet(extractedJson, path);
            if (aiVal !== null && aiVal !== undefined) next[path] = aiVal;
            else delete next[path];
            return next;
        });
    }, [extractedJson]);

    const handleSave = useCallback(async () => {
        if (!item?.analysisItemId || !hasEditor) return;
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const values = {};

            if (isLegalItem) {
                const fields = getLegalFields(legalType);
                fields.forEach((field) => {
                    const v = editValues[field.path];
                    if (v !== undefined && v !== '' && v !== null) {
                        values[field.path] = serializeFieldValue(field, v);
                    }
                });
            } else {
                editSections.forEach(({ fields }) => {
                    fields.forEach(({ path, type }) => {
                        const v = editValues[path];
                        if (v !== undefined && v !== '' && v !== null) {
                            values[path] = type === 'number' ? Number(v) : v;
                        }
                    });
                });
            }

            const saveCategory = isLegalItem
                ? categoryForType(legalType)
                : item.category;

            const res = await axios.post(
                `${url}/document-analysis/items/${item.analysisItemId}/operational-overrides`,
                {
                    category: saveCategory,
                    save_reason: saveReason || 'Corrección manual desde revisión de discrepancias',
                    values,
                    clear_paths: clearPathsQueue,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOverrideAudit(res.data.audit_summary || overrideAudit);
            const newPaths = (res.data.overrides || []).map(o => o.field_path);
            setActiveOverridePaths(newPaths);
            setClearPathsQueue([]);
            setSaveReason('');
            setEditMode(false);
            onSaved?.();
        } catch {
            // keep edit mode open so user can retry
        } finally {
            setSaving(false);
        }
    }, [item?.analysisItemId, item?.category, hasEditor, isLegalItem, legalType, editSections, editValues, saveReason, clearPathsQueue, overrideAudit, onSaved]);

    // Helper to safely render string vs object info
    const CURRENCY_KEYS = new Set(['saldo_inicial', 'saldo_final', 'total_cargos', 'total_abonos', 'saldo_promedio']);
    const CURRENCY_PARENT_KEYS = new Set(['balance_general', 'estado_resultados']);
    const fmtCurrency = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', currencyDisplay: 'narrowSymbol' }).format(Number(v));
    const humanLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const renderExtractedValue = (val, key) => {
        if (key && CURRENCY_KEYS.has(key) && val != null && val !== '' && !isNaN(Number(val))) return fmtCurrency(val);
        return String(val ?? 'No legible / No encontrado');
    };

    const isPM = persona && (persona.selectedTypePerson === 2 || persona.tipo_persona === 'PM' || persona.typePerson === 2);
    const isAvalDoc = item?.category?.toUpperCase().includes('AVAL') || (isPM && item?.category?.toUpperCase().includes('IDENTIFICACIONES'));
    const isInvalidType = extractedJson.es_documento_valido === false;

    const systemData = React.useMemo(() => {
        const ci = persona?.company_info;
        if (isAvalDoc && ci?.aval) {
            return [
                { label: 'Nombre del Aval / Rep. Legal', value: ci.aval.nombre },
                { label: 'RFC', value: ci.aval.rfc },
                { label: 'Correo Electrónico', value: ci.aval.email },
                { label: 'Teléfono', value: ci.aval.phone },
            ];
        }
        if (isPM && ci?.empresa) {
            return [
                { label: 'Razón Social', value: ci.empresa.razon_social || persona?.name_syntage || persona?.name },
                { label: 'RFC', value: ci.empresa.rfc },
                { label: 'Dirección', value: ci.empresa.direccion },
                { label: 'Correo Electrónico', value: ci.empresa.email || persona?.email },
                { label: 'Teléfono', value: ci.empresa.phone || persona?.phone },
            ];
        }
        if (persona) {
            return [
                { label: 'Nombre completo', value: persona.name_syntage || persona.name },
                { label: 'RFC', value: persona.rfc || ci?.empresa?.rfc },
                { label: 'Dirección', value: ci?.empresa?.direccion },
                { label: 'Correo Electrónico', value: persona.email },
                { label: 'Teléfono', value: persona.phone },
            ];
        }
        return null;
    }, [item, persona, isPM, isAvalDoc]);

    const parsedFindings = React.useMemo(() => {
        if (!item?.finding) return [];
        return Array.isArray(item.finding) ? item.finding.map(f => {
            if (typeof f === 'string') return { message: f, is_valid: item.status === 'ok' && !isInvalidType };
            return f;
        }) : [];
    }, [item, isInvalidType]);

    return (
        <AnimatePresence>
            {isOpen && item && (
                <div
                    onClick={onClose}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', padding: '1rem',
                    }}
                >
                    <motion.div
                        className="fk-root"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        style={{
                            backgroundColor: '#ffffff', borderRadius: '1rem',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                            width: '95%', maxWidth: '80rem', display: 'flex', flexDirection: 'column',
                            height: '90vh', overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            borderBottom: '1px solid #e2e8f0', padding: '1rem 1.5rem',
                        }}>
                            <div>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#0f172a', margin: '0 0 0.25rem 0' }}>
                                    Revisión de Discrepancias
                                </h2>
                                <p style={{ fontSize: '0.875rem', color: '#64748b', margin: 0 }}>
                                    Comparando datos extraídos vs. datos del cliente
                                </p>
                            </div>
                            <AppIconButton icon="pi pi-times" size="1.25rem" onClick={onClose} title="Cerrar" />
                        </div>

                        {/* Split Body */}
                        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                            {/* Left: Document Preview */}
                            <div style={{
                                width: '50%', borderRight: '1px solid #e2e8f0', backgroundColor: '#e2e8f0',
                                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {item.url ? (
                                    item.url.toLowerCase().includes('.pdf') ? (
                                        <iframe
                                            src={`${item.url}#toolbar=0&navpanes=0&scrollbar=1`}
                                            width="100%" height="100%"
                                            style={{ border: 'none', minHeight: '100%' }}
                                            title="Documento"
                                        />
                                    ) : (
                                        <img
                                            src={item.url} alt="Documento"
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                        />
                                    )
                                ) : (
                                    <div style={{ color: '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                        <i className="pi pi-file" style={{ fontSize: '3rem' }}></i>
                                        <span>No hay vista previa disponible</span>
                                    </div>
                                )}
                            </div>

                            {/* Right: Content */}
                            <div style={{ padding: '1.5rem', overflowY: 'auto', width: '50%', backgroundColor: '#f8fafc' }}>
                                {/* Invalid doc banner */}
                                {isInvalidType && (
                                    <div style={{
                                        marginBottom: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                                        borderRadius: '0.5rem', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    }}>
                                        <i className="pi pi-times-circle" style={{ color: '#dc2626', fontSize: '1.5rem' }}></i>
                                        <div>
                                            <h3 style={{ margin: 0, color: '#991b1b', fontSize: '0.9rem', fontWeight: 'bold' }}>DOCUMENTO NO VÁLIDO</h3>
                                            <p style={{ margin: '0.125rem 0 0 0', color: '#b91c1c', fontSize: '0.8rem' }}>
                                                La IA ha detectado que este archivo no pertenece a la categoría <strong>{item.category}</strong>.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Hallazgos */}
                                <div style={{
                                    marginBottom: '1rem',
                                    backgroundColor: isInvalidType ? '#ffffff' : '#fff7ed',
                                    border: `1px solid ${isInvalidType ? '#e2e8f0' : '#fed7aa'}`,
                                    borderRadius: '0.5rem', padding: '1rem',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#f59e0b', marginBottom: '0.5rem' }}>
                                        <i className="pi pi-exclamation-triangle"></i>
                                        <h3 style={{ fontWeight: 'bold', color: '#7c2d12', margin: 0, fontSize: '1rem' }}>Hallazgos de la IA</h3>
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: '0', listStyleType: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {parsedFindings.map((f, i) => (
                                            <li key={i} style={{
                                                fontSize: '0.875rem',
                                                color: f.is_valid ? '#166534' : '#9a3412',
                                                fontWeight: f.is_valid ? 'normal' : '500',
                                                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                            }}>
                                                <i className={`pi ${f.is_valid ? 'pi-check-circle' : 'pi-exclamation-triangle'}`}
                                                    style={{ color: f.is_valid ? '#22c55e' : '#f59e0b', marginTop: '2px' }}></i>
                                                <span>{f.message}</span>
                                            </li>
                                        ))}
                                        {parsedFindings.length === 0 && (
                                            <li style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic' }}>
                                                No hay hallazgos reportados.
                                            </li>
                                        )}
                                    </ul>
                                </div>

                                {/* Edit / View toggle bar */}
                                {hasEditor && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                                        {!editMode ? (
                                            <AppButton
                                                icon="pi pi-pencil"
                                                variant="info"
                                                size="sm"
                                                onClick={() => setEditMode(true)}
                                                disabled={loadingOverrides}
                                                loading={loadingOverrides}
                                            >
                                                Editar datos operativos
                                                {overrideAudit?.has_overrides && (
                                                    <span style={{
                                                        backgroundColor: `${TEAL}18`, color: TEAL,
                                                        fontSize: '0.65rem', padding: '0.1rem 0.4rem',
                                                        borderRadius: '9999px', fontWeight: '700',
                                                    }}>
                                                        {overrideAudit.override_count} confirmados
                                                    </span>
                                                )}
                                            </AppButton>
                                        ) : (
                                            <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <i className="pi pi-pencil" style={{ fontSize: '0.8rem', color: TEAL }} />
                                                Modo edición
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Edit form OR data comparison */}
                                <AnimatePresence mode="wait">
                                {editMode && hasEditor ? (
                                    <motion.div
                                        key="edit-form"
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                        style={{
                                            backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
                                            borderRadius: '0.75rem', padding: '1rem',
                                            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                                        }}
                                    >
                                        {isLegalItem ? (
                                            <div>
                                                <StructuredLegalEditor
                                                    schema={legalSchema}
                                                    editValues={editValues}
                                                    activeOverridePaths={activeOverridePaths}
                                                    accentColor={TEAL}
                                                    onFieldChange={handleFieldChange}
                                                    onClearPath={handleClearPath}
                                                />
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    <AppTextField
                                                        label="Motivo de corrección"
                                                        value={saveReason}
                                                        onChange={e => setSaveReason(e.target.value)}
                                                        placeholder="Ej: Dato incorrecto en OCR..."
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                                                    <AppButton label="Cancelar" variant="secondary" size="sm" onClick={() => setEditMode(false)} disabled={saving} />
                                                    <AppButton label="Guardar correcciones" variant="primary" size="sm" onClick={handleSave} loading={saving} />
                                                </div>
                                            </div>
                                        ) : (
                                            <DiscrepanciaEditForm
                                                sections={editSections}
                                                editValues={editValues}
                                                activeOverridePaths={activeOverridePaths}
                                                missingRequiredPaths={item?.operational_completeness?.missing_required_paths || []}
                                                saveReason={saveReason}
                                                saving={saving}
                                                onFieldChange={handleFieldChange}
                                                onClearPath={handleClearPath}
                                                onSaveReasonChange={setSaveReason}
                                                onSave={handleSave}
                                                onCancel={() => setEditMode(false)}
                                                aiValues={aiValues}
                                            />
                                        )}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="comparison-view"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.15 }}
                                        style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1fr)', gap: '1.5rem' }}
                                    >
                                        {/* Datos del sistema */}
                                        <div style={{
                                            backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
                                            borderRadius: '0.75rem', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', overflow: 'hidden',
                                        }}>
                                            <div style={{
                                                backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0',
                                                padding: '0.5rem 1rem', fontWeight: '600', color: '#334155',
                                                fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            }}>
                                                <span>Datos Registrados en Sistema</span>
                                                {isAvalDoc && <span style={{ backgroundColor: '#f59e0b', color: '#fff', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '700' }}>AVAL</span>}
                                                {!isAvalDoc && isPM && <span style={{ backgroundColor: '#8b5cf6', color: '#fff', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: '700' }}>EMPRESA</span>}
                                            </div>
                                            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                {systemData ? (
                                                    systemData.map(({ label, value }, i) => (
                                                        <div key={i}>
                                                            <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
                                                            <p style={{ fontSize: '0.875rem', color: '#0f172a', margin: '0.25rem 0 0 0' }}>{value || 'N/A'}</p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ fontSize: '0.875rem', color: '#94a3b8', fontStyle: 'italic' }}>Información de sistema no disponible.</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Datos IA */}
                                        <div style={{
                                            backgroundColor: '#ffffff', borderRadius: '0.75rem',
                                            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05), 0 0 0 1px rgba(14,165,233,0.2)', overflow: 'hidden',
                                        }}>
                                            <div style={{
                                                backgroundColor: '#f0f9ff', borderBottom: '1px solid #e0f2fe',
                                                padding: '0.5rem 1rem', fontWeight: '600', color: '#075985',
                                                fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            }}>
                                                <span>Datos Extraídos por IA</span>
                                                <i className="pi pi-sparkles" style={{ fontSize: '0.875rem' }}></i>
                                            </div>
                                            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                {Object.keys(extractedJson).length > 0 ? (
                                                    Object.entries(extractedJson).map(([key, rawValue], i) => {
                                                        // Nested object → render as sub-table
                                                        if (rawValue != null && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
                                                            const entries = Object.entries(rawValue).filter(([, v]) => v !== null && v !== undefined);
                                                            if (entries.length === 0) return null;
                                                            const isCurrencyParent = CURRENCY_PARENT_KEYS.has(key);
                                                            return (
                                                                <div key={i}>
                                                                    <label style={{
                                                                        fontSize: '0.72rem', fontWeight: '700', color: '#475569',
                                                                        textTransform: 'uppercase', letterSpacing: '0.05em',
                                                                        borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem',
                                                                        display: 'block', marginBottom: '0.35rem',
                                                                    }}>
                                                                        {humanLabel(key)}
                                                                    </label>
                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.1rem 0.75rem' }}>
                                                                        {entries.map(([k, v]) => {
                                                                            const isNum = v != null && v !== '' && !isNaN(Number(v));
                                                                            const path = `${key}.${k}`;
                                                                            const isOverridden = activeOverridePaths.includes(path);
                                                                            const overrideValue = isOverridden ? editValues[path] : null;

                                                                            const formatVal = (val, asNum) => {
                                                                                if (val == null || val === '') return 'Sin datos';
                                                                                if (asNum) return (isCurrencyParent ? fmtCurrency(val) : new Intl.NumberFormat('es-MX', { maximumFractionDigits: 4 }).format(Number(val)));
                                                                                if (typeof val === 'object') return JSON.stringify(val);
                                                                                return String(val);
                                                                            };

                                                                            const formattedAI = formatVal(v, isNum);
                                                                            const formattedOverride = isOverridden ? formatVal(overrideValue, typeof overrideValue === 'number' || !isNaN(Number(overrideValue))) : null;
                                                                            const isEffectivelyOverridden = isOverridden && formattedAI !== formattedOverride;

                                                                            return (
                                                                                <React.Fragment key={k}>
                                                                                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{humanLabel(k)}</span>
                                                                                    <span style={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 600, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                                                                        {isEffectivelyOverridden ? (
                                                                                            <>
                                                                                                <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.7rem' }}>{formattedAI}</span>
                                                                                                <span style={{ color: TEAL, backgroundColor: `${TEAL}18`, padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{formattedOverride}</span>
                                                                                            </>
                                                                                        ) : (
                                                                                            <span>{formattedAI}</span>
                                                                                        )}
                                                                                    </span>
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        // Array → comma-separated
                                                        if (Array.isArray(rawValue)) {
                                                            const items = rawValue.map(v => typeof v === 'string' ? v : JSON.stringify(v));
                                                            return (
                                                                <div key={i}>
                                                                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{humanLabel(key)}</label>
                                                                    <div style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#0f172a' }}>{items.join(', ') || 'Sin datos'}</div>
                                                                </div>
                                                            );
                                                        }
                                                        // Scalar value
                                                        const isOverridden = activeOverridePaths.includes(key);
                                                        const overrideValue = isOverridden ? editValues[key] : null;

                                                        const val = renderExtractedValue(rawValue, key);
                                                        const isValid = val !== 'No legible / No encontrado' && val !== 'null' && val.trim() !== '';
                                                        
                                                        const overriddenVal = isOverridden ? renderExtractedValue(overrideValue, key) : null;
                                                        const isEffectivelyOverridden = isOverridden && val !== overriddenVal;

                                                        return (
                                                            <div key={i}>
                                                                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{humanLabel(key)}</label>
                                                                <div style={{ margin: '0.25rem 0 0 0', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                                    <span style={{
                                                                        fontSize: '0.875rem',
                                                                        color: isValid ? (isEffectivelyOverridden ? '#94a3b8' : '#0f172a') : '#dc2626',
                                                                        fontWeight: isValid ? 'normal' : '500',
                                                                        backgroundColor: isValid ? 'transparent' : '#fef2f2',
                                                                        padding: isValid ? '0' : '0.125rem 0.5rem',
                                                                        borderRadius: '0.25rem',
                                                                        display: 'inline-block',
                                                                        wordBreak: 'break-all',
                                                                        textDecoration: isEffectivelyOverridden ? 'line-through' : 'none'
                                                                    }}>
                                                                        {val}
                                                                    </span>
                                                                    {isEffectivelyOverridden && (
                                                                        <span style={{
                                                                            fontSize: '0.875rem',
                                                                            color: TEAL,
                                                                            fontWeight: '600',
                                                                            backgroundColor: `${TEAL}18`,
                                                                            padding: '0.125rem 0.5rem',
                                                                            borderRadius: '0.25rem',
                                                                            display: 'inline-block',
                                                                            wordBreak: 'break-all'
                                                                        }}>
                                                                            {overriddenVal}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div style={{ fontSize: '0.875rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                        No hay JSON extraído por la IA.
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Datos confirmados manualmente */}
                                        {overrideAudit?.has_overrides && (
                                            <div style={{
                                                backgroundColor: '#f0fdf4', borderRadius: '0.75rem', padding: '0.75rem 1rem',
                                                border: '1px solid #bbf7d0', display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                                            }}>
                                                <i className="pi pi-check-circle" style={{ color: '#16a34a', fontSize: '1rem', marginTop: '0.1rem' }} />
                                                <div>
                                                    <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#15803d', marginBottom: '0.2rem' }}>
                                                        Datos confirmados manualmente ({overrideAudit.override_count} {overrideAudit.override_count === 1 ? 'campo' : 'campos'})
                                                    </div>
                                                    {overrideAudit.last_confirmed_by && (
                                                        <div style={{ fontSize: '0.75rem', color: '#166534' }}>
                                                            Última corrección por <strong>{overrideAudit.last_confirmed_by}</strong>
                                                            {overrideAudit.last_confirmed_at && (
                                                                <> el {new Date(overrideAudit.last_confirmed_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            borderTop: '1px solid #e2e8f0', padding: '1rem 1.5rem', backgroundColor: '#ffffff',
                            display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
                        }}>
                            <AppButton label="Cerrar y Revisar Después" variant="secondary" onClick={onClose} />
                            <AppButton label="Marcar como Válido Temporalmente" icon="pi pi-check" variant="primary" />
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default DiscrepanciaModal;
